import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import { AccessibilityProvider } from "@/components/editor/AccessibilityProvider";
import { UndoRedoProvider } from "@/components/editor/UndoRedoProvider";
import { TouchOptimizationProvider } from "@/components/editor/TouchOptimization";
import { CollaborationProvider } from "@/components/editor/CollaborationProvider";
import { VisualFeedbackProvider } from "@/components/editor/VisualFeedback/VisualFeedbackProvider";
import type { AnimationPerformanceMetrics } from "@/components/editor/VisualFeedback";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  ProjectGuard,
} from "@/components/permissions/PermissionGuard";
import { PermissionAction } from "@/types/permissions";
import { ResponsiveAdaptationFramework } from "@/components/mobile/ResponsiveAdaptationFramework";
import { MultiViewportCanvas } from "@/components/editor/MultiViewportCanvas";
import { RightSidebar } from "@/components/editor/RightSidebar";
import { PaletteScope } from "@/components/editor/PaletteScope";
import { MultiPageEditorToolbar } from "@/components/editor/MultiPageEditorToolbar";
import { EditorPermissionManager } from "@/components/editor/EditorPermissionManager";
import { VersioningPanel } from "@/components/editor/VersioningPanel";
import { FullPageVersioningInterface } from "@/components/editor/FullPageVersioningInterface";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useProjectAutoSave } from "@/hooks/useProjectAutoSave";
import { useUserFlowIntegration } from "@/hooks/useUserFlowIntegration";
import { useKeyboardShortcuts } from "@/hooks/editor/useKeyboardShortcuts";
import { socketClient } from '@/services/websocket/socketClient';
import { ProjectsApi } from "@/services/api/projects";
import { config } from "@/config";
import { ProjectSharingDialog } from "@/components/project/ProjectSharingDialog";
import { Project } from "@/types";
import { logger } from "@/utils";

import {
  WorkflowEditorProvider,
  WorkflowCanvas,
  WorkflowSidebar,
} from "@/components/workflow/editor";

import {
  ChatContainer,
  QueryInput,
  QueryResultsPanel,
  SourcesPanel,
  HistoryPanel,
  SavedQueryChips,
  saveQueryToHistory,
} from "@/components/data";
import { useDataQuery } from "@/hooks/data/useDataQuery";
import type { ChatMessage } from "@/hooks/data/useDataQuery";
import { useDataSources } from "@/hooks/data/useDataSources";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Database, ChevronRight, Play, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { queryTemplateApi } from "@/services/api/queryTemplateApi";
import { DataTemplateSelector } from "@/components/editor/DataTemplateSelector";

// ---------- Data Editor Panel ----------
interface DataEditorPanelProps {
  onClose: () => void;
  registerDataActions: (actions: NonNullable<ReturnType<typeof useEditorStore.getState>['dataActions']>) => void;
  clearDataActions: () => void;
  toolbarNode: React.ReactNode;
  projectName?: string;
}

const DataEditorPanel: React.FC<DataEditorPanelProps> = ({
  onClose,
  registerDataActions,
  clearDataActions,
  toolbarNode,
  projectName,
}) => {
  const { messages, isQuerying, sendQuery, addMessage, error: queryError } = useDataQuery();
  const { sources, isLoading: sourcesLoading } = useDataSources();
  const dataSourceId = useEditorStore((state) => state.dataSourceId);
  const [activeTab, setActiveTab] = useState('query');
  const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null);
  const [isRunningTemplate, setIsRunningTemplate] = useState(false);

  const handleRunTemplate = useCallback(async () => {
    if (!dataSourceId || isRunningTemplate) return;
    setIsRunningTemplate(true);
    try {
      const result = await queryTemplateApi.execute(dataSourceId, {}, { skipCache: true });
      const resultMessage: ChatMessage = {
        id: `template-${Date.now()}`,
        role: 'assistant',
        content: `Template executed successfully — ${result.metadata.rowCount} row${result.metadata.rowCount !== 1 ? 's' : ''} returned in ${result.metadata.executionTimeMs}ms.`,
        timestamp: new Date(),
        data: result.data,
        source: { id: result.metadata.templateId, name: result.metadata.templateName },
        debug: { generatedQuery: result.metadata.resolvedVariables, executionTimeMs: result.metadata.executionTimeMs },
        success: true,
      };
      addMessage(resultMessage);
      setSelectedMessage(resultMessage);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Template execution failed';
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: `Template execution failed: ${errorMsg}`,
        timestamp: new Date(),
        isError: true,
      };
      addMessage(errorMessage);
      setSelectedMessage(errorMessage);
    } finally {
      setIsRunningTemplate(false);
    }
  }, [dataSourceId, isRunningTemplate, addMessage]);

  // Auto-select latest assistant message with debug data
  useEffect(() => {
    const assistantMessages = messages.filter(
      (m) => m.role === 'assistant' && m.debug
    );
    if (assistantMessages.length > 0) {
      setSelectedMessage(assistantMessages[assistantMessages.length - 1]);
    }
  }, [messages]);

  // Ref keeps latest callbacks without triggering re-renders or effects
  const dataStateRef = useRef({ sendQuery, setActiveTab });
  dataStateRef.current = { sendQuery, setActiveTab };

  // Register action bridge ONCE on mount — callbacks proxy through ref to always get latest values.
  // Only stable callbacks are stored (no reactive data) to avoid infinite loop:
  // registerDataActions → store update → EditorLayout re-render → DataEditorPanel re-render → loop.
  useEffect(() => {
    registerDataActions({
      handleExecuteQuery: (q: string) => dataStateRef.current.sendQuery(q),
      setActiveTab: (tab: string) => dataStateRef.current.setActiveTab(tab),
    });
    return () => { clearDataActions(); };
  }, []);

  // ESC to exit data mode
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSendQuery = useCallback((query: string) => {
    sendQuery(query);
    saveQueryToHistory(query);
  }, [sendQuery]);

  return (
    <div className="w-full h-full flex min-w-0 animate-in fade-in-0 duration-300">
      {/* Center column */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {toolbarNode}
        <div className="flex-1 bg-muted/20 overflow-hidden p-4">
          {/* Canvas Card — mirrors ResponsiveEditorCanvas Card wrapper */}
          <Card className="flex flex-col h-full overflow-hidden rounded-lg border-0 bg-background shadow-none">
            {/* Canvas Header — breadcrumb + DataTemplateSelector */}
            <div
              className="flex items-center justify-between px-4 py-2.5 border-b"
              style={{
                backgroundColor: 'hsl(var(--primary) / 0.03)',
                borderColor: 'hsl(var(--primary) / 0.19)',
              }}
            >
              <div className="flex items-center gap-2">
                <div className="w-1 h-5 rounded-full bg-primary" />
                <nav className="flex items-center gap-1" aria-label="Breadcrumb">
                  <span className="text-sm font-medium text-muted-foreground">
                    {projectName || 'Project'}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/50" />
                  <span className="flex items-center gap-1.5 text-sm font-medium text-primary">
                    <Database className="w-3.5 h-3.5" />
                    Data
                  </span>
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5"
                  disabled={!dataSourceId || isRunningTemplate}
                  onClick={handleRunTemplate}
                >
                  {isRunningTemplate
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    : <Play className="w-3.5 h-3.5" />}
                  Run
                </Button>
                <DataTemplateSelector />
              </div>
            </div>

            {/* Results content — fills remaining space */}
            <div className="flex-1 overflow-y-auto">
              <QueryResultsPanel selectedMessage={selectedMessage} />
            </div>
          </Card>
        </div>
      </div>

      {/* Right sidebar */}
      <div className="w-64 lg:w-72 xl:w-80 h-full bg-card border-l border-border flex-shrink-0 overflow-hidden flex flex-col">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
          <TabsList className="w-full justify-start px-2 pt-2 shrink-0">
            <TabsTrigger value="query" className="text-xs">Query</TabsTrigger>
            <TabsTrigger value="history" className="text-xs">History</TabsTrigger>
            <TabsTrigger value="sources" className="text-xs">Sources</TabsTrigger>
          </TabsList>

          <TabsContent value="query" className="flex-1 flex flex-col overflow-hidden m-0 p-0">
            <div className="flex-1 overflow-y-auto">
              <ChatContainer
                messages={messages}
                isQuerying={isQuerying}
                onFollowUpClick={handleSendQuery}
              />
            </div>
            <div className="px-2 py-1 shrink-0">
              <SavedQueryChips onSelect={handleSendQuery} />
            </div>
            <div className="px-2 pb-2 shrink-0">
              <QueryInput onSubmit={handleSendQuery} isLoading={isQuerying} />
            </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 overflow-hidden m-0 p-0">
            <HistoryPanel
              onSelect={handleSendQuery}
              onClose={() => setActiveTab('query')}
            />
          </TabsContent>

          <TabsContent value="sources" className="flex-1 overflow-hidden m-0 p-0">
            <SourcesPanel
              sources={sources}
              isLoading={sourcesLoading}
              onClose={() => setActiveTab('query')}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// ---------- Editor Page ----------
interface EditorPageProps {
  showSharingDialog?: boolean;
  setShowSharingDialog?: (show: boolean) => void;
  showPermissionManager?: boolean;
  setShowPermissionManager?: (show: boolean) => void;
  onOpenPermissionMatrix?: () => void;
  onOpenProjectSettings?: () => void;
  onOpenSharing?: () => void;
  onToggleVersioning?: () => void;
  onExitVersioningMode?: () => void;
  isVersioningOpen?: boolean;
  isVersioningMode?: boolean;
}

export const EditorPage: React.FC<EditorPageProps> = ({
  showSharingDialog: externalShowSharingDialog,
  setShowSharingDialog: externalSetShowSharingDialog,
  showPermissionManager: externalShowPermissionManager,
  setShowPermissionManager: externalSetShowPermissionManager,
  onOpenPermissionMatrix: externalOnOpenPermissionMatrix,
  onOpenProjectSettings: externalOnOpenProjectSettings,
  onOpenSharing: externalOnOpenSharing,
  onToggleVersioning,
  onExitVersioningMode,
  isVersioningOpen,
  isVersioningMode,
}) => {
  const { user, isAuthenticated, tokens } = useAuthStore();
  const { extendSession } = useAuthSession();
  const { restoreEditorState, joinCollaboration } =
    useUserFlowIntegration();

  // Initialize keyboard shortcuts for component operations (delete, copy, paste, etc.)
  useKeyboardShortcuts();
  const navigate = useNavigate();

  // Workflow mode state
  const editorMode = useEditorStore((state) => state.editorMode);
  const workflowId = useEditorStore((state) => state.workflowId);
  const exitWorkflowMode = useEditorStore((state) => state.exitWorkflowMode);
  const exitDataMode = useEditorStore((state) => state.exitDataMode);
  const registerDataActions = useEditorStore((state) => state.registerDataActions);
  const clearDataActions = useEditorStore((state) => state.clearDataActions);
  const { projectId, pageId } = useParams<{
    projectId: string;
    pageId?: string;
  }>();

  // State
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalShowSharingDialog, setInternalShowSharingDialog] =
    useState(false);
  const [internalShowPermissionManager, setInternalShowPermissionManager] =
    useState(false);

  // WebSocket connection tracking refs for enhanced StrictMode protection
  const socketOwnerIdRef = useRef(`editor-page-${Math.random().toString(36).slice(2)}`);
  const connectionAttemptRef = useRef<string | null>(null);
  const connectionCooldownRef = useRef<number>(0);
  const editorProjectId = projectId ?? "";

  // WebSocket connection management with enhanced StrictMode protection
  useEffect(() => {
    if (!config.features.realTimeCollaboration) {
      return;
    }

    if (isAuthenticated && user && tokens?.accessToken && projectId) {
      // Enhanced connection protection with cooldown
      const now = Date.now();
      const cooldownPeriod = 1000; // 1 second cooldown between connections

      if (now - connectionCooldownRef.current < cooldownPeriod) {
        logger.info("⏳ WebSocket connection cooldown active, skipping", {
          remainingMs: cooldownPeriod - (now - connectionCooldownRef.current)
        });
        return;
      }

      // Prevent multiple connection attempts for the same configuration
      const connectionKey = `${user.id}-${projectId}`;
      if (connectionAttemptRef.current === connectionKey && socketClient.isConnected()) {
        logger.info("🔒 WebSocket already connected for this configuration", {
          userId: user.id,
          projectId,
          connectionKey
        });
        return;
      }

      logger.info("🚀 Initializing WebSocket connection", {
        userId: user.id,
        projectId,
        hasToken: !!tokens.accessToken,
        connectionKey
      });

      connectionAttemptRef.current = connectionKey;
      connectionCooldownRef.current = now;

      void socketClient.acquireConnection(socketOwnerIdRef.current, {
        url: config.websocket.url,
        token: tokens.accessToken,
        userId: user.id,
        reconnectionAttempts: config.websocket.reconnectAttempts,
        reconnectionDelay: config.websocket.reconnectInterval,
        mobileOptimizations: {
          enableBatteryOptimization: true,
          enableOfflineMode: true,
          reducedUpdateFrequency: false, // Keep full frequency for editor
          compressionEnabled: true,
          prioritizeEssentialUpdates: true,
          adaptiveReconnection: true,
          backgroundSyncEnabled: true,
          networkAwareSync: true,
        }
      }).catch((connectError) => {
        logger.error("Editor WebSocket connect failed", {
          projectId,
          userId: user.id,
          error:
            connectError instanceof Error
              ? connectError.message
              : String(connectError),
        });
      });
    }

    return () => {
      // Only disconnect if we're the ones who initiated the connection
      const connectionKey = user ? `${user.id}-${projectId}` : null;
      if (connectionKey === connectionAttemptRef.current) {
        logger.info("🧹 Releasing WebSocket ownership on cleanup", { connectionKey });
        socketClient.releaseConnection(socketOwnerIdRef.current);
        connectionAttemptRef.current = null;
      }
    };
  }, [
    isAuthenticated,
    user,
    tokens?.accessToken,
    projectId,
  ]);

  // Session recovery and lifecycle management
  useEffect(() => {
    // Handle page refresh and navigation
    const handleBeforeUnload = () => {
      if (socketClient.isConnected()) {
        logger.info("Gracefully disconnecting WebSocket on page unload");
        socketClient.disconnect({ clearOwners: true });
      }
    };

    // Handle page visibility changes for mobile/desktop
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden - reduce activity
        logger.info("Page hidden - entering background mode");
      } else {
        // Page is visible - restore activity
        logger.info("Page visible - exiting background mode");
        // Ensure WebSocket is still connected
        if (isAuthenticated && user && tokens?.accessToken && projectId && !socketClient.isConnected()) {
          logger.info("Reconnecting WebSocket after page visibility");
          void socketClient.acquireConnection(socketOwnerIdRef.current, {
            url: config.websocket.url,
            token: tokens.accessToken,
            userId: user.id,
          }).catch((error: unknown) => {
            logger.error("Editor visibility reconnect failed", {
              projectId,
              userId: user.id,
              error: error instanceof Error ? error.message : String(error),
            });
          });
        }
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, user, tokens?.accessToken, projectId]);

  // Use external state if provided, otherwise use internal state
  const showSharingDialog =
    externalShowSharingDialog ?? internalShowSharingDialog;
  const setShowSharingDialog =
    externalSetShowSharingDialog ?? setInternalShowSharingDialog;
  const showPermissionManager =
    externalShowPermissionManager ?? internalShowPermissionManager;
  const setShowPermissionManager =
    externalSetShowPermissionManager ?? setInternalShowPermissionManager;

  // Use external callbacks if provided, otherwise use state setters
  const handleOpenSharing =
    externalOnOpenSharing || (() => setShowSharingDialog(true));
  const handleOpenPermissionMatrix =
    externalOnOpenPermissionMatrix || (() => setShowPermissionManager(true));
  const handleOpenProjectSettings =
    externalOnOpenProjectSettings ||
    (() => {
      // TODO: Implement project settings dialog
      console.log("Open project settings");
    });


  // Redirect unauthenticated users
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load project data with enhanced session validation
  useEffect(() => {
    if (isAuthenticated && projectId) {
      // Validate session before loading project
      extendSession();
      loadProject();
    }
  }, [isAuthenticated, projectId, extendSession]);

  // Handle page switching when pageId changes in URL
  useEffect(() => {
    const editorStore = useEditorStore.getState();

    if (
      project &&
      project.isMultiPage &&
      pageId &&
      pageId !== "undefined" &&
      editorStore.currentPageId !== pageId
    ) {
      // Use store's unwrapped pages instead of project.pages
      const targetPage = editorStore.pages.find((p) => p.id === pageId);
      if (targetPage) {
        editorStore.switchToPage(pageId);
        logger.info("Switched page via URL change", {
          pageId,
          pageName: targetPage.name,
        });
      } else {
        logger.warn("Page not found in store", {
          pageId,
          availablePages: editorStore.pages.map((p) => ({
            id: p.id,
            name: p.name,
          })),
        });
      }
    }
  }, [pageId, project]);

  const loadProject = async () => {
    if (!projectId) {
      setError("No project ID provided");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Extend session before loading project
      extendSession();

      const response = await ProjectsApi.getProject(projectId);
      if (response.success && response.data) {
        setProject(response.data);
        logger.info("Project loaded successfully", {
          projectId,
          projectName: response.data.name,
        });

        // Set project in editor store (this will handle multi-page initialization)
        useEditorStore.getState().setProject(response.data);

        // Set org context for data queries when editing org-owned projects
        if (response.data.owner_type === 'Organization' && response.data.owner_id) {
          useViewerStore.setState({ viewerOrgId: response.data.owner_id });
        }

        // Join collaboration if WebSocket is connected
        if (socketClient.isConnected()) {
          logger.info("Joining collaboration for loaded project", {
            projectId: response.data.id,
            userId: user?.id
          });
          await joinCollaboration(response.data.id);
        }

        // Handle page-specific routing for multi-page projects
        if (response.data.isMultiPage && response.data.pages?.length > 0) {
          const editorStore = useEditorStore.getState();

          if (pageId) {
            // If pageId is in URL, switch to that specific page using unwrapped pages
            const targetPage = editorStore.pages.find((p) => p.id === pageId);
            if (targetPage) {
              editorStore.switchToPage(pageId);
              logger.info("Switched to specific page", {
                pageId,
                pageName: targetPage.name,
              });
            } else {
              // Page not found, redirect to default page using unwrapped pages
              const defaultPage =
                editorStore.pages.find((p) => p.isDefault) ||
                editorStore.pages[0];
              if (defaultPage) {
                navigate(`/editor/${projectId}/page/${defaultPage.id}`, {
                  replace: true,
                });
                return; // Exit early to avoid double initialization
              }
            }
          } else {
            // No pageId in URL, redirect to default page for multi-page projects using unwrapped pages
            const defaultPage =
              editorStore.pages.find((p) => p.isDefault) ||
              editorStore.pages[0];
            if (defaultPage) {
              navigate(`/editor/${projectId}/page/${defaultPage.id}`, {
                replace: true,
              });
              return; // Exit early to avoid double initialization
            }
          }
        } else {
          // Handle legacy single-page projects - no components property check needed
          // Components are now handled through pages structure
          logger.info("Single-page project loaded without pages structure");
        }

        // Restore editor state if available
        const savedState = restoreEditorState();
        if (savedState) {
          logger.info("Restored previous editor state");
        }

        // Note: Collaboration is automatically initialized by CollaborationProvider
        // No need to manually start it here to avoid duplicate triggers
      } else {
        setError("Failed to load project");
      }
    } catch (err) {
      logger.error("Error loading project:", err);
      setError("Failed to load project");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoize save callbacks to prevent dependency chain recreation
  const handleSaveSuccess = useCallback((timestamp: Date) => {
    logger.info("Auto-save completed", { projectId, timestamp });
  }, [projectId]);

  const handleSaveError = useCallback((error: Error) => {
    logger.error("Auto-save failed", { projectId, error });
  }, [projectId]);

  // Auto-save functionality
  const autoSave = useProjectAutoSave({
    projectId: editorProjectId,
    enabled: Boolean(projectId && project && isAuthenticated), // Enable when project is loaded and user authenticated
    autoSaveInterval: project?.settings?.autoSaveInterval
      ? project.settings.autoSaveInterval * 1000
      : 30000,
    onSaveSuccess: handleSaveSuccess,
    onSaveError: handleSaveError,
  });

  // Save timing and cleanup on unmount - MUST be after autoSave hook declaration
  useEffect(() => {
    return () => {
      // Get fresh component data from store to avoid race conditions
      const currentState = useEditorStore.getState();

      if (currentState.isModified && projectId && autoSave?.saveProject) {
        // Force save with current store state
        autoSave.saveProject("pre-unmount").catch((error) => {
          console.error("Pre-unmount save failed:", error);
        });
      }

      // Clear org context to prevent stale state after leaving editor
      useViewerStore.setState({ viewerOrgId: null });
    };
  }, [projectId, autoSave]);


  // Memoized callbacks to prevent infinite loops

  const handleVisualFeedbackPerformanceIssue = useCallback((
    metrics: AnimationPerformanceMetrics,
  ) => {
    logger.warn("Visual feedback performance issue", metrics);
  }, []);

  const handleVisualFeedbackError = useCallback((error: Error) => {
    logger.error("Visual feedback system error", error);
  }, []);

  // Don't render editor if user is not authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-muted-foreground">Loading project...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Error Loading Project</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <button
            onClick={() => navigate("/projects")}
            className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  // Don't render if no project loaded
  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Project Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The project you're looking for doesn't exist or you don't have
            access to it.
          </p>
          <button
            onClick={() => navigate("/projects")}
            className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
          >
            Back to Projects
          </button>
        </div>
      </div>
    );
  }

  return (
    <ProjectGuard
      projectId={editorProjectId}
      action={PermissionAction.READ}
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
            <p className="text-muted-foreground mb-4">
              You don't have permission to access this project.
            </p>
            <button
              onClick={() => navigate("/projects")}
              className="bg-primary text-primary-foreground px-4 py-2 rounded hover:bg-primary/90"
            >
              Back to Projects
            </button>
          </div>
        </div>
      }
      loading={
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">Checking permissions...</p>
          </div>
        </div>
      }
    >
      <AccessibilityProvider>
        <UndoRedoProvider>
          <TouchOptimizationProvider>
            <CollaborationProvider
              currentUser={user}
              projectId={projectId}
              autoConnect={isAuthenticated && !!user && !!projectId}
            >
              <ResponsiveAdaptationFramework>
                      <VisualFeedbackProvider
                        debugMode={process.env.NODE_ENV === "development"}
                        onPerformanceIssue={
                          handleVisualFeedbackPerformanceIssue
                        }
                        onError={handleVisualFeedbackError}
                      >
                        <TooltipProvider>
                        <div className="w-full h-full flex transition-all duration-300 ease-in-out overflow-hidden">
                          {isVersioningMode ? (
                            /* Full-Page Versioning Mode */
                            <div className="w-full flex flex-col min-w-0 animate-in fade-in-0 slide-in-from-bottom-2 duration-300">
                              {/* Multi-Page Editor Toolbar - Always visible */}
                              <MultiPageEditorToolbar
                                project={project}
                                onOpenPermissionMatrix={
                                  handleOpenPermissionMatrix
                                }
                                onOpenSharing={handleOpenSharing}
                                onOpenProjectSettings={
                                  handleOpenProjectSettings
                                }
                                onToggleVersioning={onToggleVersioning}
                                isVersioningOpen={isVersioningOpen}
                                isVersioningMode={isVersioningMode}
                              />

                              {/* Full-Page Versioning Interface */}
                              <div className="flex-1 relative">
                                <FullPageVersioningInterface
                                  onClose={onExitVersioningMode || (() => {})}
                                />
                              </div>
                            </div>
                          ) : editorMode === 'workflow' ? (
                            /* Workflow Mode - Same layout pattern as normal editor mode */
                            <WorkflowEditorProvider workflowId={workflowId || undefined} embedded onClose={exitWorkflowMode} projectId={projectId}>
                              <div className="w-full h-full flex min-w-0 animate-in fade-in-0 duration-300">
                                {/* Center — same flex-col structure as normal editor mode */}
                                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                  <MultiPageEditorToolbar
                                    project={project}
                                    onOpenPermissionMatrix={handleOpenPermissionMatrix}
                                    onOpenSharing={handleOpenSharing}
                                    onOpenProjectSettings={handleOpenProjectSettings}
                                    onToggleVersioning={onToggleVersioning}
                                    isVersioningOpen={isVersioningOpen}
                                    isVersioningMode={isVersioningMode}
                                  />
                                  {/* Canvas area — overflow-hidden (ReactFlow handles panning) */}
                                  <div className="flex-1 bg-muted/20 overflow-hidden">
                                    <WorkflowCanvas />
                                  </div>
                                </div>

                                {/* Right Sidebar — properties, variables, log, AI */}
                                <div className="w-64 lg:w-72 xl:w-80 h-full bg-card border-l border-border flex-shrink-0 overflow-hidden">
                                  <WorkflowSidebar />
                                </div>
                              </div>
                            </WorkflowEditorProvider>
                          ) : editorMode === 'data' ? (
                            /* Data Mode - Same layout pattern as workflow mode */
                            <DataEditorPanel
                              onClose={exitDataMode}
                              registerDataActions={registerDataActions}
                              clearDataActions={clearDataActions}
                              projectName={project?.name}
                              toolbarNode={
                                <MultiPageEditorToolbar
                                  project={project}
                                  onOpenPermissionMatrix={handleOpenPermissionMatrix}
                                  onOpenSharing={handleOpenSharing}
                                  onOpenProjectSettings={handleOpenProjectSettings}
                                  onToggleVersioning={onToggleVersioning}
                                  isVersioningOpen={isVersioningOpen}
                                  isVersioningMode={isVersioningMode}
                                />
                              }
                            />
                          ) : (
                            /* Normal Editor Mode */
                            <div className="w-full h-full flex min-w-0 animate-in fade-in-0 duration-300">
                              {/* Main Editor Area - Flexible width */}
                              <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                {/* Multi-Page Editor Toolbar */}
                                <MultiPageEditorToolbar
                                  project={project}
                                  onOpenPermissionMatrix={
                                    handleOpenPermissionMatrix
                                  }
                                  onOpenSharing={handleOpenSharing}
                                  onOpenProjectSettings={
                                    handleOpenProjectSettings
                                  }
                                  onToggleVersioning={onToggleVersioning}
                                  isVersioningOpen={isVersioningOpen}
                                  isVersioningMode={isVersioningMode}
                                />

                                {/* Canvas Area - Scoped with color palette */}
                                <div className="flex-1 min-h-0 bg-muted/30 overflow-hidden">
                                  <PaletteScope
                                    paletteParams={{ projectId }}
                                    className="h-full"
                                  >
                                    <MultiViewportCanvas />
                                  </PaletteScope>
                                </div>
                              </div>

                              {/* Right Sidebar - Fixed width, responsive */}
                              <div className="w-[448px] lg:w-[504px] xl:w-[560px] h-full bg-card border-l border-border flex-shrink-0 overflow-hidden">
                                <RightSidebar />
                              </div>
                            </div>
                          )}
                        </div>
                        </TooltipProvider>
                      </VisualFeedbackProvider>
              </ResponsiveAdaptationFramework>
            </CollaborationProvider>
          </TouchOptimizationProvider>
        </UndoRedoProvider>
      </AccessibilityProvider>

      {/* Project Sharing Dialog */}
      {project && (
        <ProjectSharingDialog
          project={project}
          isOpen={showSharingDialog}
          onClose={() => setShowSharingDialog(false)}
        />
      )}

      {/* Permission Manager Dialog */}
      <EditorPermissionManager
        isOpen={showPermissionManager}
        onClose={() => setShowPermissionManager(false)}
      />

      {/* Version History Modal */}
      <VersioningPanel
        isOpen={isVersioningOpen || false}
        onClose={onToggleVersioning || (() => {})}
        onSaveSnapshot={(name?: string) => autoSave?.saveProject?.("snapshot-panel", name)}
        onRestoreComplete={() => autoSave?.cancelAndResetAutoSave?.()}
      />
    </ProjectGuard>
  );
};
